import React, { useMemo, useState } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import { useAudio } from 'react-use';

const steps = ['visual', 'auditory', 'color-and-calculation', 'media-task', 'finished-task'];

interface ClientInstructionsVrProps {
  setOpen: Function;
}

const soundImgStyle = {
  cursor: 'pointer',
};

const SINGLE_SOUND_URL = 'assets/tutorial/auditory_single.mp3';
const DOUBLE_SOUND_URL = 'assets/tutorial/auditory_double.mp3';

const ClientInstructionsVr: React.FC<ClientInstructionsVrProps> = ({ setOpen }) => {
  const { t, i18n } = useTranslation();

  const [audio, state, controls] = useAudio({
    src: SINGLE_SOUND_URL,
  });

  const [doubleAudio, dState, dControls] = useAudio({
    src: DOUBLE_SOUND_URL,
  });

  const [currentStep, setCurrentStep] = useState(steps[0]);

  const nextIndex = useMemo(() => {
    const foundIndex = steps.findIndex(item => item === currentStep);
    if (foundIndex !== -1 && foundIndex < steps.length - 1) {
      return foundIndex + 1;
    }
    return null;
  }, [currentStep]);

  const handleClickNext = () => {
    if (nextIndex) {
      setCurrentStep(steps[nextIndex]);
    } else {
      setCurrentStep(steps[0]);
      setOpen(false);
    }
  };

  const ColorAndCalculationTask = useMemo(() => {
    if (currentStep !== steps[2]) {
      return null;
    }
    return (
      <div className="calculation-container font-mono">
        <h4 className="text-xl font-semibold mb-4">{t('color-and-calculation-task')}</h4>
        <div className="instruction-row">
          <h4 className="text-lg">{t('color')}</h4>
          <p>{t('moving-ball-description')}</p>
          <div className="center">
            <img src={'assets/tutorial/EMDR-remote_sphere.png'} height="50px" alt={''} />
          </div>
        </div>
        <div className="instruction-row">
          <h4 className="text-lg">{t('calculation-task')}</h4>
          <p>{t('calculation-task-description')}</p>
          <div className="calculation-container">
            <p>{t('calculation-task-example')}</p>
          </div>
        </div>
      </div>
    );
  }, [currentStep, i18n, t]);

  const AudioTask = useMemo(() => {
    if (currentStep !== steps[1]) {
      return null;
    }
    return (
      <div className="instruction-row">
        <h4 className="text-xl font-semibold mb-4">{t('auditory-task')}</h4>
        <p>
          {t('begin-sound')}
          <a onClick={() => controls.play()}>{t('this-sound')}</a>
          {t('end-sound')}
        </p>
        <div className="instruction-row sub-row">
          <div className="sub-row-container">
            <img
              alt={'push-down'}
              style={soundImgStyle}
              src={'assets/tutorial/cue_sound_once.png'}
              onClick={() => controls.play()}
            />
            <p>{t('1-push-touchpad')}</p>
          </div>
          <div className="sub-row-container">
            <img
              alt={'push-down'}
              style={soundImgStyle}
              src={'assets/tutorial/cue_sound_double.png'}
              onClick={() => dControls.play()}
            />
            <p>{t('2-push-touchpad')}</p>
          </div>
        </div>
        <div className="vr-controller relative">
          <p className="mr-0">{t('touchpad')}</p>
          <img alt={'keyboard-down'} src={'assets/tutorial/controller_touchpad.png'} />
        </div>
      </div>
    );
  }, [currentStep, controls, i18n, t]);

  const VisualTask = useMemo(() => {
    if (currentStep !== steps[0]) {
      return null;
    }
    return (
      <div className="instruction-row font-mono">
        <h4 className="text-xl font-semibold mb-4">{t('reaction-task')}</h4>
        <p>{t('reaction-description')}</p>
        <div className="instruction-row sub-row">
          <div className="sub-row-container">
            <img alt={''} src={'assets/tutorial/click_100.png'} />
            <p>{t('1-push-trigger')}</p>
          </div>
          <div className="sub-row-container">
            <img alt={''} src={'assets/tutorial/doubleclick_100.png'} />
            <p>{t('2-push-trigger')}</p>
          </div>
          <div className="sub-row-container">
            <img alt={''} src={'assets/tutorial/noclick_100.png'} />
            <p>{t('do-nothing')}</p>
          </div>
        </div>
        <div className="vr-controller center">
          <p>{t('trigger')}</p>
          <img alt={''} src={'assets/tutorial/controller_trigger.png'} />
        </div>
      </div>
    );
  }, [currentStep, i18n, t]);

  const MediaTask = useMemo(() => {
    if (currentStep !== steps[3]) {
      return null;
    }
    return (
      <div className="instruction-row font-mono">
        <h4 className="text-xl font-semibold mb-4">{t('media-task')}</h4>
        <p>
          <Trans i18nKey="media-task-description-part-1" components={{ br: <br /> }} />
        </p>
        <p>{t('media-task-description-part-2')}</p>
        <div className="vr-controller center">
          <p>{t('trigger')}</p>
          <img alt="" src={'assets/tutorial/controller_trigger.png'} />
        </div>
      </div>
    );
  }, [currentStep, t, i18n]);

  const FinishedTask = useMemo(() => {
    if (currentStep !== steps[4]) {
      return null;
    }
    return (
      <div className="instruction-row font-mono finished">
        <h4 className="text-xl font-semibold mb-4">{t('finish-task-title')}</h4>
        <div className="instruction-row">
          <p>{t('finish-description')}</p>
        </div>
      </div>
    );
  }, [currentStep, i18n, t]);

  return (
    <div className="instructions">
      {audio}
      {doubleAudio}
      {VisualTask}
      {AudioTask}
      {ColorAndCalculationTask}
      {MediaTask}
      {FinishedTask}
      <div className="button-container">
        <button
          className="mt-2 bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded focus:outline-none"
          onClick={handleClickNext}
        >
          {nextIndex ? t('next') : t('close')}
        </button>
      </div>
    </div>
  );
};

export default ClientInstructionsVr;
